import { Formik } from 'formik';
import { useEffect, useState } from 'react';

import { CloseButton } from '@components/ActionButtons/CloseButton';
import { Box } from '@components/Box';
import {
  FormButtonsContainer,
  FormSectionContainer,
} from '@components/Container';
import {
  CancelButton,
  DeleteButton,
  FormikCheckbox,
  FormikSelect,
  FormikTextArea,
  SubmitButton,
} from '@components/Form';
import { FormikInput } from '@components/Form/FormikInput';
import { Loading } from '@components/Loading';
import { Space } from '@components/Space';
import { useNotification } from '@hooks/useNotification';
import { CategoryValidation, CategoryDefaultValue } from '@models/Category';
import { FormikSelectOption } from '@models/common/FormikSelectOption';
import { CategoryService } from '@services/Category';

interface Props {
  categoryId?: number;
  onDelete?: () => void;
  onClose?: (refreshGrid?: boolean) => void;
}

export const CategoryEdit: React.FC<Props> = ({
  categoryId,
  onClose,
  onDelete,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [category, setCategory] = useState(CategoryDefaultValue);
  const [parentCategories, setParentCategories] = useState<
    FormikSelectOption[]
  >([]);
  const { showError, showSuccess } = useNotification();

  const loadCategory = async () => {
    const result = await CategoryService.getById(Number(categoryId));

    if (result.isSuccess && 'data' in result) {
      setCategory(result.data);
      setIsLoading(false);
      console.log('Success: Fetched the record.');
    } else {
      console.error('Error: Failed to get the record.', result.errorMessages);
      showError(`${result.errorMessages}`);
      onClose && onClose();
    }
  };

  const loadParentCategories = async () => {
    const result = await CategoryService.getParentCategorySelectOptions(
      Number(categoryId)
    );
    if (result.isSuccess && 'data' in result) {
      const parentCategorySelections = [{ value: '0', text: '' }].concat(
        result.data.map((row) => ({
          value: row.value,
          text: row.text,
        }))
      );
      setParentCategories(parentCategorySelections);
    } else {
      console.error(
        'Error: Failed to get parent categories.',
        result.errorMessages
      );
      showError(`${result.errorMessages}`);
    }
  };

  useEffect(() => {
    loadCategory();
    loadParentCategories();
  }, []);

  if (isLoading) {
    return <Loading />;
  } else {
    return (
      <>
        <CloseButton onClick={onClose} />

        <Box size="medium">
          <Formik
            initialValues={category}
            validationSchema={CategoryValidation}
            validateOnBlur={true}
            validateOnChange={true}
            onSubmit={(values, actions) => {
              CategoryService.update(Number(categoryId), values)
                .then((result) => {
                  if (result.isSuccess) {
                    showSuccess('Successfully updated the record.');
                    onClose && onClose(true);
                  } else {
                    console.error(
                      'Error: Failed to update record.',
                      result.errorMessages
                    );
                    showError(`${result.errorMessages}`);
                  }
                })
                .finally(() => {
                  actions.setSubmitting(false);
                });
            }}
          >
            {(formikProps) => {
              return (
                <>
                  <form method="POST" onSubmit={formikProps.handleSubmit}>
                    <FormSectionContainer>
                      <FormikInput label="Name" name="name" />

                      <FormikSelect
                        label="Parent Category"
                        name="parentId"
                        selection={parentCategories}
                      />
                      <FormikTextArea label="Description" name="description" />
                      <FormikCheckbox label="Is Active" name="active" />
                      <Space />
                    </FormSectionContainer>
                    <FormButtonsContainer>
                      <div className="flex space-x-4">
                        <CancelButton onClick={onClose} />

                        <DeleteButton onClick={onDelete} />

                        <SubmitButton
                          label="Save"
                          disabled={
                            formikProps.isSubmitting || !formikProps.isValid
                          }
                        />
                      </div>
                    </FormButtonsContainer>
                  </form>
                </>
              );
            }}
          </Formik>
        </Box>
      </>
    );
  }
};
